// ============================================================
// 🚚 VELOCITY SHIPPING — shared frontend helpers
// ============================================================
// Status text, gating rules aur role check ek hi jagah, taaki har Velocity
// component same rules follow kare. Source: docs/VELOCITY_FRONTEND_API.md
// (backend repo, branch fintach_phase1).

/** Order par jo courier name backend save karta hai. */
export const VELOCITY_COURIER_KEY = 'velocity';
export const VELOCITY_COURIER_NAME = 'Velocity';

/** Backend defaults — ship/rates body khali ho to yahi use hote hain. */
export const VELOCITY_DEFAULT_BOX = Object.freeze({
  weightKg: 0.5,
  lengthCm: 20,
  breadthCm: 15,
  heightCm: 10,
});

/** Label ek pre-signed S3 URL hai jo ~1 ghante me expire hota hai. */
export const VELOCITY_LABEL_TTL_NOTE =
  'This label link expires in about 1 hour. Download or print it now.';

/**
 * Velocity shipment status → user ko dikhne wala text.
 * Ye mapping backend doc se as-is li gayi hai; yahan apna mapping mat banao.
 */
export const VELOCITY_STATUS_LABELS = Object.freeze({
  pending: 'Courier pending',
  ready_for_pickup: 'Ready for pickup',
  pickup_scheduled: 'Ready for pickup',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  ndr_raised: 'Delivery failed (NDR)',
  need_attention: 'Delivery failed (NDR)',
  rto_initiated: 'Returning to warehouse (RTO)',
  rto_in_transit: 'Returning to warehouse (RTO)',
  rto_delivered: 'Returning to warehouse (RTO)',
  cancellation_requested: 'Cancelling…',
  cancelled: 'Cancelled',
  lost: 'Lost',
  return_pickup_scheduled: 'Return: pickup scheduled',
  return_in_transit: 'Return: in transit',
  return_delivered: 'Return: received',
});

/** Jo status doc me nahi hai (processing, not_picked…) use padhne layak bana do. */
export const velocityStatusLabel = (status) => {
  if (!status) return '—';
  const key = String(status).toLowerCase();
  if (VELOCITY_STATUS_LABELS[key]) return VELOCITY_STATUS_LABELS[key];
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
};

/** Pill ka rang chunne ke liye — component classes map karta hai. */
export const velocityStatusTone = (status) => {
  const key = String(status || '').toLowerCase();
  if (key === 'delivered' || key === 'return_delivered') return 'success';
  if (key === 'cancelled' || key === 'lost') return 'danger';
  if (key === 'ndr_raised' || key === 'need_attention') return 'warn';
  if (key.startsWith('rto_')) return 'warn';
  if (key === 'cancellation_requested') return 'warn';
  if (key === 'pending' || key === 'processing' || key === 'not_picked') return 'muted';
  return 'progress';
};

/** Cancel sirf pickup se pehle allowed hai. */
export const VELOCITY_CANCELLABLE_STATUSES = Object.freeze([
  'pending',
  'processing',
  'ready_for_pickup',
  'pickup_scheduled',
  'not_picked',
]);

export const isVelocityShipmentCancellable = (shipment) =>
  !!shipment &&
  shipment.direction !== 'RETURN' &&
  VELOCITY_CANCELLABLE_STATUSES.includes(String(shipment.status || '').toLowerCase());

/** Ship button sirf VERIFIED / CONFIRMED order par. */
export const VELOCITY_SHIPPABLE_ORDER_STATUSES = Object.freeze(['VERIFIED', 'CONFIRMED']);

export const canShipOrderWithVelocity = (orderStatus) =>
  VELOCITY_SHIPPABLE_ORDER_STATUSES.includes(String(orderStatus || '').toUpperCase());

/** Return sirf DELIVERED order par. */
export const canReturnOrderWithVelocity = (orderStatus) =>
  String(orderStatus || '').toUpperCase() === 'DELIVERED';

/**
 * ship / create-order / assign-courier / cancel / return — backend (commit
 * 0dd1621) in teeno roles ko allow karta hai. Badle to sirf yahi line badalni hai.
 */
export const VELOCITY_BOOKING_ROLES = Object.freeze(['ADMIN', 'MANAGER', 'SUPERVISOR']);

/** localStorage user ka role/designation — PermissionContext wala hi idiom. */
export const readUserRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return String(user?.role || user?.designation || '').toUpperCase();
  } catch {
    return '';
  }
};

/**
 * Ship / cancel / return buttons dikhane hain ya nahi.
 * Role na milne par 403 aayega, isliye button chhupa dete hain.
 */
export const hasVelocityBookingRole = (role = readUserRole()) =>
  VELOCITY_BOOKING_ROLES.some((r) => String(role || '').includes(r));

/** /velocity/warehouse aur /velocity/sync sirf ADMIN ke liye hain. */
export const hasVelocityAdminRole = (role = readUserRole()) => String(role || '').includes('ADMIN');

/**
 * Tracking link. Asli URL backend ke shipment object me `trackUrl` field me aata
 * hai; wo null ho to hi ye prefix use hota hai. List pages par shipment object
 * nahi hota, isliye wahan yahi fallback lagta hai.
 */
export const VELOCITY_TRACK_URL_FALLBACK = 'https://shipfastt.in/track/';

export const velocityTrackUrl = (shipment) => {
  if (!shipment) return null;
  if (shipment.trackUrl) return shipment.trackUrl;
  return shipment.awbCode ? `${VELOCITY_TRACK_URL_FALLBACK}${shipment.awbCode}` : null;
};

/** Order Velocity se ship hua hai? (backend courier name "VELOCITY" save karta hai) */
export const isVelocityOrder = (order) => {
  if (!order) return false;
  const name = String(order.courierName || order.courier || order.courierKey || '');
  return name.toUpperCase().includes('VELOCITY');
};

/** List me se dikhane layak shipment — newest FORWARD jo cancelled na ho. */
export const pickActiveVelocityShipment = (shipments = []) => {
  const list = Array.isArray(shipments) ? shipments : [];
  const forward = list.filter((s) => s?.direction !== 'RETURN');
  return (
    forward.find((s) => !['cancelled', 'cancellation_requested'].includes(String(s?.status || '').toLowerCase())) ||
    forward[0] ||
    list[0] ||
    null
  );
};

/**
 * Backend ka message user ke liye likha hua hai — usko as-is dikhao.
 * 502 (timeout / Velocity down) par extra hint add karte hain.
 */
export const velocityErrorMessage = (err, { action = 'request' } = {}) => {
  const msg = err?.message || 'Something went wrong';
  if (err?.status === 502) {
    return action === 'ship' || action === 'cancel'
      ? `${msg} — check the Velocity panel before trying again, the request may have gone through.`
      : `${msg} — try again in a few minutes.`;
  }
  return msg;
};

/** 409 = Velocity par book ho gaya par CRM order update fail hua. Ship dobara mat karo. */
export const isVelocityBookedButUnsaved = (err) => err?.status === 409;

/** Ship / rates / preview body — khali fields bheje bina. */
export const buildVelocityBox = (box = {}) => {
  const out = {};
  ['weightKg', 'lengthCm', 'breadthCm', 'heightCm'].forEach((k) => {
    const n = Number(box[k]);
    if (Number.isFinite(n) && n > 0) out[k] = n;
  });
  return out;
};

/** Charges JSON raw aata hai; jo mila usi ka total bana do. */
export const velocityTotalCharges = (charges) => {
  if (!charges || typeof charges !== 'object') return null;
  const fwd = charges.frwd_charges || charges.forward_charges || {};
  const nums = [fwd.shipping_charges, fwd.cod_charges]
    .map((v) => parseFloat(v))
    .filter((v) => Number.isFinite(v));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0);
};
